import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorState } from '@fiado/react-kit';
import { platformApi } from '../../api/endpoints/platform';
import { resolveErrorMessage } from '../../api/errorMapping';
import { buildLabelResolver } from '../../hooks/useLabelResolver';
import { levelName } from '../../lib/scopeLabels';
import { ContentLoading } from '../../components/shared/ContentLoading';
import { LevelEntitiesTable } from '../../pages/platform/tenants/levels/LevelEntitiesTable';
import { LevelParentScope } from '../../pages/platform/tenants/levels/LevelParentScope';
import { isLevelName } from '../../pages/platform/tenants/levels/levelConfig';
import type { LevelNameLocal } from '../../api/types/LevelEntityLocal';

/**
 * Pestaña de un nivel del drill-down (`/platform/tenants/{tenantId}/levels/{level}`). LEVEL_2/3 se
 * gestionan dentro de un padre elegido en `LevelParentScope`.
 */
export const Route = createFileRoute(
    '/_authenticated/_layout/platform/tenants/$tenantId/levels/$level',
)({
    beforeLoad: ({ params }) => {
        if (!isLevelName(params.level)) throw notFound();
    },
    component: TenantLevel,
});

function TenantLevel() {
    const { tenantId, level } = Route.useParams();

    const {
        data: tenant,
        isPending,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
    });

    const labels = useMemo(() => buildLabelResolver({ levels: tenant?.levels }), [tenant]);

    // El beforeLoad ya garantizó que `level` es un LevelName válido.
    if (!isLevelName(level)) return null;
    if (isPending) return <ContentLoading />;
    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar este nivel"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    const nameOf = (lvl: LevelNameLocal): string => levelName(labels, lvl);
    // Si el nivel es de ID externo en el tenant visto, el alta pide y manda ese id.
    const requireExternalId = tenant.externalScopeLevels?.includes(level) ?? false;
    const renderTable = (parentId?: string) => (
        <LevelEntitiesTable
            tenantId={tenantId}
            level={level}
            levelDisplayName={nameOf(level)}
            requireExternalId={requireExternalId}
            parentId={parentId}
        />
    );

    if (level === 'LEVEL_1') return renderTable();
    return (
        <LevelParentScope tenantId={tenantId} level={level} nameOf={nameOf}>
            {renderTable}
        </LevelParentScope>
    );
}
